import React from 'react';

import { Badge } from '../Badge';
import { Text } from '../Text/Text';
import type { SyncOutcome, SyncProposal } from '../../services/requirementSyncApi';
import { OUTCOME_TEXT, diffWords, fieldLabel, formatValue, type DiffPart } from './syncReviewFormat';

/** ``parts``: the value's words with the changed ones marked (removed in Before, added in After). */
export const ValueBox: React.FC<{ label: string; value: unknown; tone: 'old' | 'new'; parts?: DiffPart[] }> = ({
  label, value, tone, parts,
}) => (
  <div className="flex flex-col gap-bg-v2-xxs min-w-0">
    <Text variant="text-xs" weight="medium" color="tertiary">{label}</Text>
    <div
      className={`rounded-bg-v2-sm px-bg-v2-md py-bg-v2-sm text-bg-v2-xs font-bg-v2-sans text-text-primary whitespace-pre-wrap break-words ${
        tone === 'old' ? 'bg-bg-error-primary' : 'bg-bg-success-primary'
      }`}
    >
      {parts
        ? parts.map((p, i) => (p.changed ? (
          <span
            key={i}
            className={`rounded-bg-v2-xs box-decoration-clone ${tone === 'old' ? 'bg-utility-error-200 line-through' : 'bg-utility-success-200'}`}
          >
            {p.text}
          </span>
        ) : <React.Fragment key={i}>{p.text}</React.Fragment>))
        : formatValue(value)}
    </div>
  </div>
);

/** Before and After side by side, the words that differ highlighted in each. */
export const BeforeAfter: React.FC<{ before: unknown; after: unknown }> = ({ before, after }) => {
  const oldText = formatValue(before);
  const newText = formatValue(after);
  // A value that is empty on one side (shown as "—") has nothing to compare word by word.
  const diff = oldText !== '—' && newText !== '—' && oldText !== newText ? diffWords(oldText, newText) : null;
  return (
    <div className="grid grid-cols-2 gap-bg-v2-md">
      <ValueBox label="Before" value={before} tone="old" parts={diff?.before} />
      <ValueBox label="After" value={after} tone="new" parts={diff?.after} />
    </div>
  );
};

/** What a CAB proposal changes, field by field: Before/After for an update, the values of a new item, or the
 *  removal. */
export const ProposalFields: React.FC<{ proposal: SyncProposal }> = ({ proposal }) => {
  if (proposal.action === 'delete') {
    return (
      <div className="flex flex-col gap-bg-v2-xs">
        <Text variant="text-sm" color="secondary">This item will be removed from the CAB.</Text>
        {proposal.stillLinked.length > 0 && (
          <Text variant="text-xs" color="warning">Open tickets that still point to it: {proposal.stillLinked.join(', ')}</Text>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-bg-v2-lg min-w-0">
      {proposal.fields.map(f => (
        <div key={f.field} className="flex flex-col gap-bg-v2-xs min-w-0">
          <Text variant="text-xs" weight="semibold" color="secondary">{fieldLabel(f.field)}</Text>
          {proposal.action === 'update' ? (
            <BeforeAfter before={f.old} after={f.removed ? null : f.new} />
          ) : (
            <ValueBox label="Value" value={f.new} tone="new" />
          )}
        </div>
      ))}
    </div>
  );
};

export const OutcomeRow: React.FC<{ outcome: SyncOutcome }> = ({ outcome }) => (
  <div className="flex flex-col gap-bg-v2-xxs min-w-0">
    <div className="flex items-center gap-bg-v2-sm flex-wrap">
      <Text variant="text-sm" weight="semibold" color="primary" as="span">{outcome.ticketId}</Text>
      {outcome.change !== 'modified' && (
        <Badge tone={outcome.change === 'created' ? 'success' : 'error'}>{outcome.change === 'created' ? 'New' : 'Deleted'}</Badge>
      )}
      <Badge tone={outcome.outcome === 'unhandled' ? 'warning' : 'neutral'}>{OUTCOME_TEXT[outcome.outcome] ?? outcome.outcome}</Badge>
    </div>
    {/* The AI Model's own note on the ticket is not shown: no free text from the AI Model in review pop-ups. */}
    <Text variant="text-xs" color="secondary" className="break-words">
      {[outcome.title, outcome.links.length ? `CAB: ${outcome.links.map(l => l.label).join(', ')}` : '']
        .filter(Boolean).join(' · ')}
    </Text>
  </div>
);
